import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { isPaidPlan } from '@/utils/planHelpers';
import { apiService, MAX_UPLOAD_BYTES } from '@/api';
import { useUserStore } from '@/store/userStore';
import { OPERATION_TYPES } from '@/types';
import { CREDIT_COST, CREDIT_COSTS } from '@/api/config';
import { Textarea, Button, Switch, Modal } from '@/components/ui';
import { AspectRatioSelector, PillSelector } from '@/components/settings';
import { PreviewLoadingScreen } from '@/components/shared';
import {
  calculateHeygenCredits,
} from '@/utils/constants';
import { PlanModal, type PlanSeed } from '@/features/plan';
import {
  MODEL_CONFIGS,
  getModelById,
  getAspectRatiosForModel,
  getImageCount,
  getModelsForOperation,
  modelIdForOperation,
  calculateCredits,
  OPERATION_LABEL_KEYS,
  SETTING_TOOLTIP_KEYS,
  MODEL_DESC_KEYS,
  type OperationType,
} from '@/utils/modelConfigs';
import { 
  Image as ImageIcon, 
  X, 
  Loader2, 
  AlertCircle,
  Plus,
  Bot,
  Wrench,
  Info,
  ChevronDown,
  FileImage,
  FileVideo,
  FileAudio,
  Upload,
  Type,
  Clapperboard,
  GalleryHorizontalEnd,
  Clock,
  Crown,
} from 'lucide-react';
import {
  trackGenerationStarted,
  trackFirstGeneration,
  trackPaywallCtaClicked,
} from '@/services/analyticsService';
import {
  trackAmpOperationSelected,
  trackAmpModelSelected,
} from '@/services/amplitudeService';

// Local storage keys
const SETTINGS_STORAGE_KEY = 'vivideo_home_settings';
const FORM_INPUTS_STORAGE_KEY = 'vivideo_home_form_inputs';
const HOME_MODE_STORAGE_KEY = 'vivideo_home_mode';
const SELECTED_MODEL_STORAGE_KEY = 'vivideo_selected_model';
const SELECTED_OPERATION_STORAGE_KEY = 'vivideo_selected_operation';
const ADVANCED_SETTINGS_STORAGE_KEY = 'vivideo_advanced_settings';

// Home mode type
type HomeMode = 'agent' | 'manual';

// Default settings
const DEFAULT_SETTINGS = {
  aspectRatio: '16:9',
  duration: '4s',
  resolution: '720p',
  generateAudio: true,
};

// Default form inputs
const DEFAULT_FORM_INPUTS = {
  prompt: '',
  imageUrls: [] as string[],
};

// Load settings from localStorage
const loadSettings = () => {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Failed to load settings:', e);
  }
  return DEFAULT_SETTINGS;
};

// Save settings to localStorage
const saveSettings = (settings: typeof DEFAULT_SETTINGS) => {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save settings:', e);
  }
};

// Load form inputs from localStorage
const loadFormInputs = () => {
  try {
    const stored = localStorage.getItem(FORM_INPUTS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        prompt: parsed.prompt || DEFAULT_FORM_INPUTS.prompt,
        imageUrls: parsed.imageUrls || DEFAULT_FORM_INPUTS.imageUrls,
      };
    }
  } catch (e) {
    console.error('Failed to load form inputs:', e);
  }
  return DEFAULT_FORM_INPUTS;
};

// Save form inputs to localStorage
const saveFormInputs = (inputs: typeof DEFAULT_FORM_INPUTS) => {
  try {
    localStorage.setItem(FORM_INPUTS_STORAGE_KEY, JSON.stringify(inputs));
  } catch (e) {
    console.error('Failed to save form inputs:', e);
  }
};

// Load home mode from localStorage
const loadHomeMode = (): HomeMode => {
  try {
    const stored = localStorage.getItem(HOME_MODE_STORAGE_KEY);
    if (stored === 'agent' || stored === 'manual') return stored;
  } catch (e) {
    console.error('Failed to load home mode:', e);
  }
  return 'agent';
};

// Save home mode to localStorage
const saveHomeMode = (mode: HomeMode) => {
  try {
    localStorage.setItem(HOME_MODE_STORAGE_KEY, mode);
  } catch (e) {
    console.error('Failed to save home mode:', e);
  }
};

// Max images for multi-image mode
const MAX_IMAGES = 3;

// ─── Mode Switcher Component ─────────────────────────────────────────────────

function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleEnter = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let left = rect.left + rect.width / 2;
    // Clamp so tooltip doesn't go off-screen (110px = half of max-width 220px)
    left = Math.max(120, Math.min(left, window.innerWidth - 120));
    setPos({ top: rect.top - 8, left });
    setShow(true);
  };

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
      onTouchStart={handleEnter}
    >
      <Info className="w-4 h-4 text-text-secondary hover:text-text-primary cursor-help transition-colors flex-shrink-0" />
      {show && createPortal(
        <div
          className="fixed px-3 py-2 rounded-lg bg-[#1F1F2E] border border-glass-border shadow-2xl text-xs text-text-primary max-w-[220px] whitespace-normal leading-relaxed animate-fade-in pointer-events-none"
          style={{ zIndex: 99999, top: pos.top, left: pos.left, transform: 'translate(-50%, -100%)' }}
        >
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px] w-2 h-2 rotate-45 bg-[#1F1F2E] border-r border-b border-glass-border" />
        </div>,
        document.body
      )}
    </div>
  );
}

function ModeSwitcher({
  mode,
  onChange,
}: {
  mode: HomeMode;
  onChange: (mode: HomeMode) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center gap-3 mb-4 md:mb-8">
      <InfoTooltip text={t('home.agentSubtitle')} />

      <div className="relative inline-flex items-center bg-surface rounded-xl p-1 border border-glass-border">
        {/* Sliding background */}
        <div
          className={cn(
            'absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-lg bg-primary/15 border border-primary/30 transition-all duration-300 ease-out',
            mode === 'agent' ? 'left-1' : 'left-[calc(50%+2px)]'
          )}
        />

        <button
          onClick={() => onChange('agent')}
          className={cn(
            'relative z-10 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 min-w-[140px]',
            mode === 'agent'
              ? 'text-text-primary'
              : 'text-text-secondary hover:text-text-primary'
          )}
        >
          <Bot className={cn('w-4 h-4', mode === 'agent' && 'text-primary')} />
          {t('home.videoAgent')}
        </button>

        <button
          onClick={() => onChange('manual')}
          className={cn(
            'relative z-10 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 min-w-[140px]',
            mode === 'manual'
              ? 'text-text-primary'
              : 'text-text-secondary hover:text-text-primary'
          )}
        >
          <Wrench className={cn('w-4 h-4', mode === 'manual' && 'text-primary')} />
          {t('home.manualCreation')}
        </button>
      </div>

      <InfoTooltip text={t('home.manualSubtitle')} />
    </div>
  );
}

// ─── Video Agent UI ──────────────────────────────────────────────────────────

// Accept image and video files — backend handles conversion to HeyGen-compatible formats
const AGENT_ACCEPT_STRING = 'image/*,video/*';

// Uploaded asset type
interface AgentAsset {
  assetId: string;
  url: string;
  fileType: 'image' | 'video' | 'audio';
  fileName: string;
  previewUrl?: string;
}

// Uploading file with preview
interface UploadingFile {
  progress: number;
  previewUrl?: string;
  fileName: string;
  fileType: 'image' | 'video' | 'audio';
}

function getFileIcon(fileType: string) {
  if (fileType === 'video') return FileVideo;
  if (fileType === 'audio') return FileAudio;
  return FileImage;
}

function getFileCategory(mimeType: string): 'image' | 'video' | 'audio' | null {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return null;
}

function isAcceptedFile(file: File): boolean {
  const type = file.type.toLowerCase();
  if (type.startsWith('image/') || type.startsWith('video/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const mediaExts = [
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'heic', 'heif',
    'mp4', 'webm', 'mov', 'avi', 'mkv', '3gp', 'flv', 'wmv', 'm4v',
  ];
  return mediaExts.includes(ext);
}

// Calculate credits for automatic video: 5 seconds = 6 credits
function calculateAutoCredits(durationSeconds: number): number {
  return Math.ceil(durationSeconds * 1.2);
}

// Format seconds as MM:SS or just seconds for short durations
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
}

// Non-linear duration steps: smaller steps at start, larger at end
const DURATION_STEPS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180];
const AUTO_DURATION_STORAGE_KEY = 'vivideo_auto_duration';
const DEFAULT_DURATION = 30; // 30 seconds default

// Duration Selection Modal for Automatic Mode
export function DurationSelectionModal({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (durationSeconds: number) => void;
}) {
  const { t } = useTranslation();
  
  const durationSteps = DURATION_STEPS;
  
  // Get initial step index from localStorage or default to 1 minute
  const getInitialStepIndex = () => {
    try {
      const stored = localStorage.getItem(AUTO_DURATION_STORAGE_KEY);
      if (stored) {
        const storedDuration = parseInt(stored, 10);
        const idx = durationSteps.indexOf(storedDuration);
        if (idx !== -1) return idx;
      }
    } catch { /* ignore */ }
    // Default to 1 minute (60s)
    const defaultIdx = durationSteps.indexOf(DEFAULT_DURATION);
    return defaultIdx !== -1 ? defaultIdx : 0;
  };
  
  const [stepIndex, setStepIndex] = useState(getInitialStepIndex);
  
  const duration = durationSteps[stepIndex];
  const credits = calculateAutoCredits(duration);
  const progress = (stepIndex / (durationSteps.length - 1)) * 100;
  
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setStepIndex(value);
    // Save to localStorage
    try {
      localStorage.setItem(AUTO_DURATION_STORAGE_KEY, String(durationSteps[value]));
    } catch { /* ignore */ }
  };
  
  const handleConfirm = () => {
    onConfirm(duration);
    onClose();
  };
  
  // Restore saved preference when modal opens
  useEffect(() => {
    if (isOpen) {
      setStepIndex(getInitialStepIndex());
    }
  }, [isOpen]);
  
  const markers = [
    { index: 0, label: '5s' },
    { index: 4, label: '30s' },
    { index: 6, label: '1m' },
    { index: 9, label: '3m' },
  ];
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('home.selectDuration')} size="sm">
      <div className="space-y-6">
        {/* Duration Display */}
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
            <Clock className="w-6 h-6 text-primary" />
            <span className="text-4xl font-bold text-text-primary tabular-nums">
              {formatDuration(duration)}
            </span>
          </div>
          <p className="text-sm text-text-secondary mt-3">
            {t('home.durationHint')}
          </p>
        </div>
        
        {/* Custom Slider */}
        <div className="px-1 pt-2 pb-6">
          {/* Slider Track Container */}
          <div className="relative">
            {/* Background Track */}
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-surface-light" />
            
            {/* Active Track */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-150"
              style={{ width: `${progress}%` }}
            />
            
            {/* Tick Marks */}
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-0">
              {durationSteps.map((_, idx) => (
                <div 
                  key={idx}
                  className={cn(
                    "w-1 h-1 rounded-full transition-colors",
                    idx <= stepIndex ? "bg-white/60" : "bg-text-tertiary/30"
                  )}
                />
              ))}
            </div>
            
            {/* Range Input */}
            <input
              type="range"
              min={0}
              max={durationSteps.length - 1}
              step={1}
              value={stepIndex}
              onChange={handleSliderChange}
              className="relative w-full h-6 appearance-none bg-transparent cursor-pointer z-10
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-6
                [&::-webkit-slider-thumb]:h-6
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-white
                [&::-webkit-slider-thumb]:shadow-lg
                [&::-webkit-slider-thumb]:shadow-primary/30
                [&::-webkit-slider-thumb]:border-2
                [&::-webkit-slider-thumb]:border-primary
                [&::-webkit-slider-thumb]:transition-transform
                [&::-webkit-slider-thumb]:hover:scale-110
                [&::-webkit-slider-thumb]:active:scale-95
                [&::-moz-range-thumb]:appearance-none
                [&::-moz-range-thumb]:w-6
                [&::-moz-range-thumb]:h-6
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-white
                [&::-moz-range-thumb]:shadow-lg
                [&::-moz-range-thumb]:shadow-primary/30
                [&::-moz-range-thumb]:border-2
                [&::-moz-range-thumb]:border-primary
                [&::-moz-range-thumb]:transition-transform
                [&::-moz-range-thumb]:hover:scale-110"
            />
          </div>
          
          {/* Markers */}
          <div className="relative mt-3 h-4">
            {markers.map(({ index, label }) => (
              <span 
                key={index}
                className={cn(
                  "absolute text-xs font-medium -translate-x-1/2 transition-colors",
                  index <= stepIndex ? "text-primary" : "text-text-tertiary"
                )}
                style={{ left: `${(index / (durationSteps.length - 1)) * 100}%` }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
        
        {/* Confirm Button */}
        <Button
          size="lg"
          className="w-full relative"
          onClick={handleConfirm}
        >
          {t('home.confirmAndCreate')}
          <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-bold bg-white/10 rounded">
            {credits} {t('account.credits').toLowerCase()}
          </span>
        </Button>
      </div>
    </Modal>
  );
}

function VideoAgentView({
  userId,
  onGenerate,
  isGenerating,
  error,
  skipDurationModal = false,
}: {
  userId: string | null;
  onGenerate: (prompt: string, duration: number, assetIds: string[]) => void;
  isGenerating: boolean;
  error: string | null;
  /**
   * v2 flow: skip the pre-generation DurationSelectionModal and hand off
   * straight to the parent (which opens PlanModal). Duration is inferred by
   * the HeyGen agent and can be revised inside the plan card.
   */
  skipDurationModal?: boolean;
}) {
  const { t } = useTranslation();
  const [agentPrompt, setAgentPrompt] = useState(() => {
    try {
      const stored = localStorage.getItem('vivideo_agent_prompt');
      return stored || '';
    } catch {
      return '';
    }
  });
  const [assets, setAssets] = useState<AgentAsset[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadingFile>>(new Map());
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showDurationModal, setShowDurationModal] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  // Persist agent prompt
  useEffect(() => {
    try {
      localStorage.setItem('vivideo_agent_prompt', agentPrompt);
    } catch {
      // ignore
    }
  }, [agentPrompt]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [agentPrompt]);

  // Upload files to HeyGen via backend
  const handleUploadFiles = useCallback(async (files: FileList | File[]) => {
    if (!userId) return;
    setUploadError(null);

    const allFiles = Array.from(files);
    // Reject anything over the hard 5 GB cap before we even look at type.
    // Multipart can technically push more than that to S3 but our worker
    // pipeline (download + ffmpeg + HeyGen upload) is sized for ≤5 GB.
    const oversized = allFiles.filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (oversized.length > 0) {
      const names = oversized.map((f) => f.name).slice(0, 3).join(', ');
      const more = oversized.length > 3 ? ` and ${oversized.length - 3} more` : '';
      setUploadError(
        `File too large: ${names}${more}. Please keep uploads under 5 GB.`
      );
    }

    const fileArray = allFiles
      .filter((f) => f.size <= MAX_UPLOAD_BYTES)
      .filter(isAcceptedFile);
    if (fileArray.length === 0) return;

    // Image formats browsers can natively render in <img> tags
    const browserRenderableTypes = new Set([
      'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp',
    ]);

    for (const file of fileArray) {
      const tempId = `${file.name}-${Date.now()}`;
      const category = getFileCategory(file.type) || 'image';

      // Only create local blob preview for formats browsers can actually render
      // HEIC, HEIF, TIFF etc. will show a file icon during upload instead
      let localPreviewUrl: string | undefined;
      if (category === 'image' && browserRenderableTypes.has(file.type.toLowerCase())) {
        try { localPreviewUrl = URL.createObjectURL(file); } catch { /* ignore */ }
      }

      setUploadingFiles(prev => new Map(prev).set(tempId, {
        progress: 0,
        previewUrl: localPreviewUrl,
        fileName: file.name,
        fileType: category as 'image' | 'video' | 'audio',
      }));

      try {
        const result = await apiService.uploadHeygenAsset(userId, file, (progress) => {
          setUploadingFiles(prev => {
            const existing = prev.get(tempId);
            if (!existing) return prev;
            return new Map(prev).set(tempId, { ...existing, progress });
          });
        });

        const resultCategory = getFileCategory(file.type) || (result.fileType as 'image' | 'video' | 'audio');

        // For the final preview: use local blob if browser can render it, otherwise use HeyGen URL (converted JPEG/PNG)
        const finalPreviewUrl = resultCategory === 'image'
          ? (localPreviewUrl || result.url)
          : undefined;

        setAssets(prev => [...prev, {
          assetId: result.assetId,
          url: result.url,
          fileType: resultCategory,
          fileName: file.name,
          previewUrl: finalPreviewUrl,
        }]);
      } catch (err) {
        console.error('Failed to upload HeyGen asset:', err);
        if (localPreviewUrl) { try { URL.revokeObjectURL(localPreviewUrl); } catch { /* ignore */ } }
        setUploadError(
          `Failed to upload ${file.name}: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setUploadingFiles(prev => {
          const next = new Map(prev);
          next.delete(tempId);
          return next;
        });
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [userId]);

  const handleRemoveAsset = (assetId: string) => {
    setAssets(prev => {
      const removed = prev.find(a => a.assetId === assetId);
      if (removed?.previewUrl?.startsWith('blob:')) {
        try { URL.revokeObjectURL(removed.previewUrl); } catch { /* ignore */ }
      }
      return prev.filter(a => a.assetId !== assetId);
    });
  };

  // Drag & drop handlers (on the whole container)
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleUploadFiles(e.dataTransfer.files);
    }
  }, [handleUploadFiles]);

  const handleSubmit = () => {
    if (!agentPrompt.trim() || isGenerating) return;
    if (skipDurationModal) {
      // v2: straight to PlanModal; duration=0 signals "agent decides".
      const assetIds = assets.map(a => a.assetId);
      onGenerate(agentPrompt.trim(), 0, assetIds);
      return;
    }
    setShowDurationModal(true);
  };

  const handleConfirmDuration = (durationSeconds: number) => {
    const assetIds = assets.map(a => a.assetId);
    onGenerate(agentPrompt.trim(), durationSeconds, assetIds);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isUploading = uploadingFiles.size > 0;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Error Messages */}
      {(error || uploadError) && (
        <div className="mb-6 p-4 rounded-xl bg-status-error/10 border border-status-error/30 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0" />
          <p className="text-sm text-status-error">{error || uploadError}</p>
        </div>
      )}

      {/* Title */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-text-primary">
          {t('home.agentTitle')}
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          {t('home.agentDescription')}
        </p>
      </div>

      {/* Agent prompt container — entire area is a drop zone */}
      <div
        className="w-full"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={cn(
          'rounded-2xl border bg-surface-light/80 backdrop-blur-sm',
          'transition-all duration-200 relative overflow-hidden',
          isDragging
            ? 'border-primary border-2 ring-2 ring-primary/20'
            : 'border-glass-border focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20'
        )}>
          {/* Drag overlay */}
          {isDragging && (
            <div className="absolute inset-0 z-20 bg-primary/5 backdrop-blur-[1px] flex flex-col items-center justify-center gap-3 pointer-events-none">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Upload className="w-7 h-7 text-primary" />
              </div>
              <p className="text-sm font-medium text-primary">{t('home.dropFilesHere')}</p>
              <p className="text-xs text-text-tertiary">{t('home.supportedFormats')}</p>
            </div>
          )}

          {/* Uploaded assets + uploading files */}
          {(assets.length > 0 || isUploading) && (
            <div className="px-5 pt-4 pb-0 flex flex-wrap gap-2">
              {/* Completed assets */}
              {assets.map((asset) => {
                const Icon = getFileIcon(asset.fileType);
                const hasImagePreview = asset.fileType === 'image' && asset.previewUrl;

                return (
                  <div
                    key={asset.assetId}
                    className={cn(
                      'relative w-16 h-16 rounded-xl overflow-hidden bg-surface border group transition-all',
                      asset.fileType === 'image' && 'border-primary/20',
                      asset.fileType === 'video' && 'border-accent-blue/20',
                      asset.fileType === 'audio' && 'border-accent-green/20'
                    )}
                  >
                    {hasImagePreview ? (
                      <img
                        src={asset.previewUrl}
                        alt={asset.fileName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={cn(
                        'w-full h-full flex flex-col items-center justify-center gap-1',
                        asset.fileType === 'video' && 'bg-accent-blue/10',
                        asset.fileType === 'audio' && 'bg-accent-green/10',
                        asset.fileType === 'image' && 'bg-primary/10'
                      )}>
                        <Icon className={cn(
                          'w-6 h-6',
                          asset.fileType === 'video' && 'text-accent-blue',
                          asset.fileType === 'audio' && 'text-accent-green',
                          asset.fileType === 'image' && 'text-primary'
                        )} />
                        <span className="text-[9px] text-text-tertiary px-1 truncate max-w-full">
                          {asset.fileName.split('.').pop()?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveAsset(asset.assetId)}
                      className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/60 hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-2.5 h-2.5 text-white" />
                    </button>
                  </div>
                );
              })}

              {/* Uploading indicators */}
              {Array.from(uploadingFiles.entries()).map(([id, info]) => {
                const hasImagePreview = info.fileType === 'image' && info.previewUrl;
                const Icon = getFileIcon(info.fileType);

                return (
                  <div
                    key={id}
                    className="relative w-16 h-16 rounded-xl overflow-hidden bg-surface border border-glass-border"
                  >
                    {hasImagePreview ? (
                      <img
                        src={info.previewUrl}
                        alt={info.fileName}
                        className="w-full h-full object-cover opacity-50"
                      />
                    ) : (
                      <div className={cn(
                        'w-full h-full flex flex-col items-center justify-center gap-1 opacity-50',
                        info.fileType === 'video' && 'bg-accent-blue/10',
                        info.fileType === 'audio' && 'bg-accent-green/10',
                        info.fileType === 'image' && 'bg-primary/10'
                      )}>
                        <Icon className={cn(
                          'w-6 h-6',
                          info.fileType === 'video' && 'text-accent-blue',
                          info.fileType === 'audio' && 'text-accent-green',
                          info.fileType === 'image' && 'text-primary'
                        )} />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-white animate-spin drop-shadow-md" />
                    </div>
                    <div
                      className="absolute bottom-0 left-0 h-1 bg-primary transition-all duration-300"
                      style={{ width: `${info.progress}%` }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Text area */}
          <div className="px-5 pt-5 pb-3">
            <textarea
              ref={textareaRef}
              value={agentPrompt}
              onChange={(e) => setAgentPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('home.describeVideoIdea')}
              maxLength={5000}
              rows={1}
              className={cn(
                'w-full bg-transparent text-text-primary placeholder-text-tertiary',
                'text-base leading-relaxed resize-none outline-none',
                'min-h-[60px] max-h-[200px]'
              )}
            />
          </div>

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between px-4 pb-4 pt-1">
            {/* Left actions */}
            <div className="flex items-center gap-1">
              {/* Add file button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className={cn(
                  'flex items-center gap-2 px-3 py-2.5 rounded-xl hover:bg-surface transition-colors',
                  'text-text-secondary hover:text-text-primary',
                  isUploading && 'opacity-50 cursor-not-allowed'
                )}
              >
                <Plus className="w-4.5 h-4.5" />
                <span className="text-sm font-medium">{t('home.addFile')}</span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept={AGENT_ACCEPT_STRING}
                multiple
                onChange={(e) => e.target.files && handleUploadFiles(e.target.files)}
                className="hidden"
              />
            </div>

          </div>
        </div>

        {/* Create Video button */}
        <div className="mt-4">
          <Button
            size="lg"
            className="w-full"
            onClick={handleSubmit}
            disabled={!agentPrompt.trim() || isGenerating || isUploading}
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {t('common.generating')}
              </>
            ) : isUploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {t('status.uploadingFile')}
              </>
            ) : (
              t('home.createVideo')
            )}
          </Button>
        </div>

        {/* File count hint */}
        {assets.length > 0 && (
          <div className="flex justify-center mt-3">
            <span className="text-xs text-text-tertiary">
              {t('home.filesAttached', { count: assets.length })}
            </span>
          </div>
        )}
      </div>

      {/* Duration Selection Modal */}
      <DurationSelectionModal
        isOpen={showDurationModal}
        onClose={() => setShowDurationModal(false)}
        onConfirm={handleConfirmDuration}
      />
    </div>
  );
}

// ─── Main HomePage Component (v1 — the original Auto/Manual UI) ──────────────
// Kept as `HomePageV1` so the new v2 UI can live beside it and be selected by
// the `abGroup`-based A/B gate in `HomePageRoot` (group 0 → v2, rest → v1).
//
// When `uiVersion='v2'`, the ONLY behavior change is the Automatic-mode
// Generate click: instead of calling /create-order directly it opens the new
// PlanModal on this same page. Everything else (mode switcher, manual mode,
// upload, manual generation) stays identical between v1 and v2.

interface HomePageV1Props {
  uiVersion?: 'v1' | 'v2';
  /** When set, auto-open PlanModal to resume the given in-flight heygen_chat order. */
  resumePlanOrderId?: string;
  /** Called once after the resume order id has been consumed (so the parent can clear ?plan=). */
  onResumeConsumed?: () => void;
}

export function HomePageV1({ uiVersion = 'v1', resumePlanOrderId, onResumeConsumed }: HomePageV1Props = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId, user, decrementCredits, hasEnoughCredits } = useUserStore();
  const isPro = isPaidPlan(user?.userPlan);

  // v2 plan-popup state
  const [planModal, setPlanModal] = useState<{
    open: boolean;
    seed?: PlanSeed;
    resumeOrderId?: string;
  }>({ open: false });

  // Auto-open PlanModal when a ?plan=<orderId> param is present (resumed from /files)
  useEffect(() => {
    if (resumePlanOrderId && uiVersion === 'v2') {
      setPlanModal({ open: true, resumeOrderId: resumePlanOrderId });
      onResumeConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumePlanOrderId, uiVersion]);

  // Home mode state
  const [homeMode, setHomeMode] = useState<HomeMode>(loadHomeMode);

  const handleHomeModeChange = (mode: HomeMode) => {
    setHomeMode(mode);
    saveHomeMode(mode);
  };

  // Model and operation selection (manual mode)
  const [selectedModelId, setSelectedModelId] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(SELECTED_MODEL_STORAGE_KEY);
      const storedOp = localStorage.getItem(SELECTED_OPERATION_STORAGE_KEY) as OperationType | null;
      const id = storedOp && stored ? modelIdForOperation(stored, storedOp) : stored;
      return id && !getModelById(id)?.hidden ? id : 'veo31_fast';
    } catch { return 'veo31_fast'; }
  });
  const [selectedOperation, setSelectedOperation] = useState<OperationType>(() => {
    try { return (localStorage.getItem(SELECTED_OPERATION_STORAGE_KEY) as OperationType) || 'text_to_video'; } catch { return 'text_to_video'; }
  });

  const selectedModel = getModelById(selectedModelId) || MODEL_CONFIGS[0];
  const [showPremiumModelModal, setShowPremiumModelModal] = useState(false);

  const FREE_MODEL_ID = 'veo31_fast';

  const handleModelChange = (modelId: string) => {
    if (!isPro && modelId !== FREE_MODEL_ID) {
      setShowPremiumModelModal(true);
      return;
    }
    setSelectedModelId(modelId);
    try { localStorage.setItem(SELECTED_MODEL_STORAGE_KEY, modelId); } catch {}
    const model = getModelById(modelId);
    trackAmpModelSelected({
      modelId,
      modelName: model?.name,
      operation: selectedOperation,
    });
    if (model && !model.operations.includes(selectedOperation)) {
      const newOp = model.operations[0];
      setSelectedOperation(newOp);
      try { localStorage.setItem(SELECTED_OPERATION_STORAGE_KEY, newOp); } catch {}
    }
    // Keep current settings if valid for new model, otherwise fallback to defaults
    if (model) {
      if (!model.durations.includes(duration)) setDuration(model.defaultDuration);
      if (!model.aspectRatios.includes(aspectRatio) && !model.aspectRatiosItv.includes(aspectRatio)) setAspectRatio(model.defaultAspectRatio);
      if (!model.resolutions.includes(settings.resolution)) updateSetting('resolution', model.defaultResolution);
    }
  };

  const handleOperationChange = (op: OperationType) => {
    setSelectedOperation(op);
    try { localStorage.setItem(SELECTED_OPERATION_STORAGE_KEY, op); } catch {}
    trackAmpOperationSelected(op);
    setImageUrls([]);
  };

  // Form state - load from localStorage
  const loadedFormInputs = loadFormInputs();
  const [prompt, setPrompt] = useState(loadedFormInputs.prompt);
  const [imageUrls, setImageUrls] = useState<string[]>(loadedFormInputs.imageUrls);
  
  // Common settings - load from localStorage with defaults
  const [settings, setSettings] = useState(loadSettings);
  const { aspectRatio, duration } = settings;

  // Helper to update individual settings
  const updateSetting = <K extends keyof typeof DEFAULT_SETTINGS>(
    key: K,
    value: typeof DEFAULT_SETTINGS[K]
  ) => {
    setSettings((prev: typeof DEFAULT_SETTINGS) => {
      const newSettings = { ...prev, [key]: value };
      saveSettings(newSettings);
      return newSettings;
    });
  };

  // Convenience setters
  const setAspectRatio = (value: string) => updateSetting('aspectRatio', value);
  const setDuration = (value: string) => updateSetting('duration', value);

  // Save form inputs to localStorage whenever they change
  useEffect(() => {
    saveFormInputs({
      prompt,
      imageUrls,
    });
  }, [prompt, imageUrls]);

  // Upload state
  const [uploadingIndexes, setUploadingIndexes] = useState<Set<number>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preview loading state
  const [showPreviewLoading, setShowPreviewLoading] = useState(false);

  // Advanced settings (model-specific) -- persisted to localStorage
  const loadedAdvanced = (() => {
    try {
      const stored = localStorage.getItem(ADVANCED_SETTINGS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  })();
  const [negativePrompt, setNegativePrompt] = useState<string>(loadedAdvanced.negativePrompt ?? '');
  const [seed, setSeed] = useState<string>(loadedAdvanced.seed ?? '');
  const [generateAudio, setGenerateAudio] = useState<boolean>(loadedAdvanced.generateAudio ?? true);
  const [autoFix, setAutoFix] = useState<boolean>(loadedAdvanced.autoFix ?? true);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(loadedAdvanced.showAdvanced ?? false);

  // Persist advanced settings whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(ADVANCED_SETTINGS_STORAGE_KEY, JSON.stringify({
        negativePrompt, seed, generateAudio, autoFix, showAdvanced,
      }));
    } catch {}
  }, [negativePrompt, seed, generateAudio, autoFix, showAdvanced]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | undefined>(undefined);

  // Image requirements for current operation
  const imageReqs = getImageCount(selectedOperation, selectedModel);

  // Validation for manual mode
  const isValid = (() => {
    if (!prompt.trim()) return false;
    if (imageReqs.min > 0 && imageUrls.length < imageReqs.min) return false;
    return true;
  })();

  // Calculate credit cost dynamically based on model, duration, and resolution
  const currentCreditCost = calculateCredits(selectedModel, duration, settings.resolution);

  // Get MIME type from file
  const getMimeType = (file: File): string => {
    if (file.type) return file.type;
    const extension = file.name.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'heic': 'image/heic',
      'heif': 'image/heif',
      'bmp': 'image/bmp',
      'tiff': 'image/tiff',
      'tif': 'image/tiff',
      'svg': 'image/svg+xml',
    };
    return mimeTypes[extension || ''] || 'application/octet-stream';
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || !userId) return;
    
    setError(null);
    const all = Array.from(files);
    const oversized = all.filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (oversized.length > 0) {
      const names = oversized.map((f) => f.name).slice(0, 3).join(', ');
      const more = oversized.length > 3 ? ` and ${oversized.length - 3} more` : '';
      setError(`File too large: ${names}${more}. Please keep uploads under 5 GB.`);
    }
    const filesToUpload = all
      .filter((f) => f.size <= MAX_UPLOAD_BYTES)
      .slice(0, MAX_IMAGES - imageUrls.length);
    
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      const uploadIndex = imageUrls.length + i;
      const fileType = getMimeType(file);
      
      setUploadingIndexes(prev => new Set(prev).add(uploadIndex));
      setUploadProgress(prev => ({ ...prev, [uploadIndex]: 0 }));

      try {
        const publicUrl = await apiService.uploadImage(
          userId,
          file,
          fileType,
          (progress) => {
            setUploadProgress(prev => ({ ...prev, [uploadIndex]: progress }));
          }
        );

        setImageUrls(prev => [...prev, publicUrl]);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(`${t('errors.errorCreatingVideo')}: ${errorMessage}`);
      } finally {
        setUploadingIndexes(prev => {
          const next = new Set(prev);
          next.delete(uploadIndex);
          return next;
        });
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove image
  const handleRemoveImage = (index: number) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
    if (imageUrls.length === 1) {
      setAspectRatio('16:9');
    }
  };

  // Handle drag and drop
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  }, [userId, imageUrls.length]);

  // Handle generation result (shared logic)
  const handleGenerationResult = async (
    generateFn: () => Promise<{ isPreview?: boolean; previewImageUrl?: string }>,
    creditCost: number,
    operationType: string,
    trackingData: {
      operationType: string;
      duration?: string;
      aspectRatio?: string;
      resolution?: string;
      hasImage?: boolean;
      imageCount?: number;
    }
  ) => {
    if (!userId) return;

    if (isPro && !hasEnoughCredits(creditCost)) {
      // No inline paywall_shown — the pricing page fires the impression on
      // arrival (with this context), so the encounter isn't counted twice.
      navigate('/subscription?context=insufficient_credits');
      return;
    }

    setIsGenerating(true);
    setError(null);

    trackGenerationStarted({ ...trackingData, mode: 'manual', freeUser: !isPro });
    trackFirstGeneration(operationType);

    const isFreeUser = !isPro;
    if (isFreeUser) {
      setShowPreviewLoading(true);
      setPreviewImageUrl(undefined);
    }

    try {
      const response = await generateFn();

      if (response.isPreview) {
        setPreviewImageUrl(response.previewImageUrl);
      } else {
        setShowPreviewLoading(false);
        decrementCredits(creditCost);
        navigate('/files?highlight=true');
      }
    } catch (err) {
      setShowPreviewLoading(false);
      const errorStr = String(err).toLowerCase();

      if (errorStr.includes('402') || errorStr.includes('insufficient_credits') || errorStr.includes('insufficient credits')) {
        navigate('/subscription?context=insufficient_credits');
        return;
      }

      const isTimeoutError = errorStr.includes('timeout') ||
                             errorStr.includes('econnaborted') ||
                             errorStr.includes('network error');

      if (isTimeoutError && !isPro) {
        navigate('/files?highlight=true');
        return;
      }

      setError(t('errors.errorCreatingVideo'));
    } finally {
      setIsGenerating(false);
    }
  };

  // Agent generate handler (always HeyGen Video Agent)
  // v1: creates an order directly via /create-order and kicks off a HeyGen render.
  // v2: opens PlanModal so the user can review/edit the plan before generation.
  const handleAgentGenerate = (_agentPrompt: string, agentDuration: number, assetIds: string[]) => {
    // Owner taxonomy: V1's agent flow counts as mode 'auto'.
    trackGenerationStarted({ operationType: 'heygen_chat', mode: 'auto', freeUser: !isPro });
    // When duration is 0, let HeyGen Video Agent decide automatically
    const effectiveDuration = agentDuration > 0 ? agentDuration : undefined;

    if (uiVersion === 'v2') {
      const seed: PlanSeed = {
        prompt: _agentPrompt,
        orientation: 'landscape',
        durationSec: effectiveDuration,
        assetIds,
      };
      // v2: plan modal now lives on /files so the Planning row shows up
      // behind it in real time. We hand off the seed via sessionStorage so
      // a soft nav is enough — no location-state fragility across refreshes.
      try {
        sessionStorage.setItem('vivideo_plan_seed', JSON.stringify(seed));
      } catch { /* quota / private-mode — FilesPage will just show an empty modal, harmless */ }
      navigate('/files?newPlan=1');
      return;
    }

    const creditCost = effectiveDuration ? calculateHeygenCredits(effectiveDuration) : CREDIT_COSTS.text_to_video || CREDIT_COST;

    const orderData: Record<string, unknown> = {
      prompt: _agentPrompt,
      resolution: '720p',
      generate_audio: true,
      video_provider: 'heygen',
    };

    // Only include duration if explicitly set
    if (effectiveDuration) {
      orderData.duration = effectiveDuration;
    }

    // Include HeyGen asset IDs for Video Agent context
    if (assetIds.length > 0) {
      orderData.files = assetIds.map(id => ({ asset_id: id }));
    }

    handleGenerationResult(
      async () => {
        const response = await apiService.createOrder(userId!, OPERATION_TYPES.TEXT_TO_VIDEO, orderData);

        // Clear agent prompt on success
        try { localStorage.removeItem('vivideo_agent_prompt'); } catch {}
        return response;
      },
      creditCost,
      OPERATION_TYPES.TEXT_TO_VIDEO,
      {
        operationType: OPERATION_TYPES.TEXT_TO_VIDEO,
        duration: effectiveDuration ? `${effectiveDuration}s` : undefined,
        resolution: '720p',
        hasImage: assetIds.length > 0,
        imageCount: assetIds.length,
      }
    );
  };

  const handlePlanApproved = useCallback((orderId: string) => {
    setPlanModal({ open: false });
    try { localStorage.removeItem('vivideo_agent_prompt'); } catch {}
    navigate(`/files?highlight=${orderId}`);
  }, [navigate]);

  // Manual generate handler
  const handleGenerate = async () => {
    if (!userId || !isValid) return;

    const opMap: Record<OperationType, string> = {
      text_to_video: OPERATION_TYPES.TEXT_TO_VIDEO,
      image_to_video: OPERATION_TYPES.IMAGE_TO_VIDEO,
      first_last_frame: 'first_frame_last_frame',
      multiple_images: OPERATION_TYPES.MULTIPLE_IMAGES,
    };
    const operationType = opMap[selectedOperation] || OPERATION_TYPES.TEXT_TO_VIDEO;
    const falModel = selectedModel.falModels[selectedOperation];

    handleGenerationResult(
      async () => {
        const orderData: Record<string, unknown> = {
          prompt: prompt.trim(),
          duration: selectedOperation === 'multiple_images' ? '8s' : settings.duration,
          aspect_ratio: settings.aspectRatio,
          resolution: settings.resolution,
          generate_audio: selectedModel.supportsAudio ? generateAudio : true,
        };

        if (falModel) {
          orderData.fal_model = falModel;
        }

        if (selectedModel.supportsNegativePrompt && negativePrompt.trim()) {
          orderData.negative_prompt = negativePrompt.trim();
        }
        if (selectedModel.supportsSeed && seed) {
          orderData.seed = parseInt(seed, 10);
        }
        if (selectedModel.supportsAutoFix) {
          orderData.auto_fix = autoFix;
        }

        if (selectedOperation === 'image_to_video') {
          orderData.image_url = imageUrls[0];
        } else if (selectedOperation === 'first_last_frame') {
          orderData.first_frame_url = imageUrls[0];
          orderData.last_frame_url = imageUrls[1];
        } else if (selectedOperation === 'multiple_images') {
          orderData.image_urls = imageUrls;
        }

        const response = await apiService.createOrder(userId!, operationType, orderData);

        setPrompt('');
        setImageUrls([]);
        saveFormInputs(DEFAULT_FORM_INPUTS);
        return response;
      },
      currentCreditCost,
      operationType,
      {
        operationType,
        duration: settings.duration,
        aspectRatio: settings.aspectRatio,
        resolution: settings.resolution,
        hasImage: imageUrls.length > 0,
        imageCount: imageUrls.length,
      }
    );
  };

  const isUploading = uploadingIndexes.size > 0;

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {/* Mode Switcher */}
      <ModeSwitcher mode={homeMode} onChange={handleHomeModeChange} />

      {/* ─── VIDEO AGENT MODE ─── */}
      {homeMode === 'agent' && (
        <VideoAgentView
          userId={userId}
          onGenerate={handleAgentGenerate}
          isGenerating={isGenerating}
          error={error}
          skipDurationModal={uiVersion === 'v2'}
        />
      )}

      {/* ─── MANUAL MODE ─── */}
      {homeMode === 'manual' && (
        <div className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-4 rounded-xl bg-status-error/10 border border-status-error/30 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0" />
              <p className="text-sm text-status-error">{error}</p>
            </div>
          )}

          {/* 1. Operation Selection */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <label className="text-sm font-semibold text-text-primary">
                {t('operations.selectOperation')}
              </label>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {(['text_to_video', 'image_to_video', 'first_last_frame', 'multiple_images'] as OperationType[]).map((op) => {
                const availableModels = getModelsForOperation(op);
                const isAvailable = availableModels.length > 0;
                const isSelected = selectedOperation === op;
                const OpIcon = op === 'text_to_video' ? Type
                  : op === 'image_to_video' ? ImageIcon
                  : op === 'first_last_frame' ? Clapperboard
                  : GalleryHorizontalEnd;

                return (
                  <button
                    key={op}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => {
                      handleOperationChange(op);
                      if (!selectedModel.operations.includes(op)) {
                        handleModelChange(availableModels[0].id);
                      }
                    }}
                    className={cn(
                      'flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all',
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : isAvailable
                          ? 'border-glass-border bg-surface-light text-text-secondary hover:border-primary/30 hover:text-text-primary'
                          : 'border-glass-border bg-surface-light/50 text-text-tertiary opacity-40 cursor-not-allowed'
                    )}
                  >
                    <OpIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{t(OPERATION_LABEL_KEYS[op])}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2. Model Selection */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <label className="text-sm font-semibold text-text-primary">{t('models.selectModel')}</label>
              <InfoTooltip text={t(SETTING_TOOLTIP_KEYS.model)} />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {getModelsForOperation(selectedOperation).map((model) => (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => handleModelChange(model.id)}
                  className={cn(
                    'flex-shrink-0 flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-xl border transition-all duration-200',
                    selectedModelId === model.id
                      ? 'border-primary bg-primary/10 shadow-glow-sm'
                      : 'border-glass-border bg-surface-light hover:border-primary/30'
                  )}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <InfoTooltip text={t(MODEL_DESC_KEYS[model.id] || model.description)} />
                  </div>
                  <img
                    src={model.brandIcon}
                    alt={model.brand}
                    className="w-5 h-5 rounded flex-shrink-0"
                  />
                  <span className={cn('text-sm font-semibold whitespace-nowrap', selectedModelId === model.id ? 'text-text-primary' : 'text-text-secondary')}>
                    {model.name}
                  </span>
                  {!isPro && model.id !== FREE_MODEL_ID && (
                    <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 3. Prompt */}
          <Textarea
            label={t('generator.prompt')}
            required
            placeholder={t('generator.describeVideoPrompt')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={2000}
            showCount
            rows={4}
          />

          {/* 3. Image Upload (shown only when operation needs images) */}
          {imageReqs.max > 0 && (
            <div>
              <div className="flex items-center gap-1 mb-2">
                <label className="text-sm font-medium text-text-secondary">{t('files.images')}</label>
                {imageReqs.min > 0 && (
                  <span className="text-xs text-status-error">(*{t('common.required')})</span>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                multiple={imageReqs.max > 1}
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
              />

              {imageReqs.labels && (
                <div className="flex gap-3 mb-3">
                  {imageReqs.labels.map((label, i) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                        {i + 1}
                      </div>
                      <span className="text-xs text-text-tertiary">{label}</span>
                    </div>
                  ))}
                </div>
              )}

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => imageUrls.length < imageReqs.max && fileInputRef.current?.click()}
                className={cn(
                  'relative rounded-2xl border-2 border-dashed transition-all cursor-pointer',
                  'min-h-[100px] flex flex-col items-center justify-center gap-2 p-4',
                  isDragging
                    ? 'border-primary bg-primary/10'
                    : imageUrls.length > 0
                      ? 'border-glass-border bg-surface-light/50'
                      : 'border-glass-border hover:border-primary/50 bg-surface-light',
                  imageUrls.length >= imageReqs.max && 'cursor-default opacity-60'
                )}
              >
                {imageUrls.length === 0 && !isUploading ? (
                  <>
                    <ImageIcon className="w-6 h-6 text-primary" />
                    <p className="text-sm text-text-secondary">{t('generator.dragDropOrClick')}</p>
                  </>
                ) : (
                  <div className="w-full">
                    <div className="flex flex-wrap gap-3">
                      {imageUrls.map((url, index) => (
                        <div
                          key={index}
                          className="relative w-20 h-20 rounded-xl overflow-hidden bg-surface border border-glass-border group"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img src={url} alt={imageReqs.labels?.[index] || `Image ${index + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                          <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-white font-medium">
                            {imageReqs.labels?.[index] || (index + 1)}
                          </div>
                        </div>
                      ))}

                      {Array.from(uploadingIndexes).map((ui) => (
                        <div key={`u-${ui}`} className="w-20 h-20 rounded-xl bg-surface border border-glass-border flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
                          <Loader2 className="w-5 h-5 text-primary animate-spin mb-1" />
                          <span className="text-xs text-text-secondary">{uploadProgress[ui] || 0}%</span>
                        </div>
                      ))}

                      {imageUrls.length < imageReqs.max && !isUploading && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} className="w-20 h-20 rounded-xl border-2 border-dashed border-glass-border hover:border-primary/50 flex items-center justify-center transition-colors">
                          <Plus className="w-5 h-5 text-text-tertiary" />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-text-tertiary mt-2 text-center">{imageUrls.length}/{imageReqs.max}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 4. Settings */}
          <div className="bg-surface-light rounded-2xl border border-glass-border p-5 space-y-5">
            {/* Aspect Ratio */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <label className="text-sm font-medium text-text-secondary">{t('generator.aspectRatio')}</label>
                <InfoTooltip text={t(SETTING_TOOLTIP_KEYS.aspectRatio)} />
              </div>
              <AspectRatioSelector
                options={getAspectRatiosForModel(selectedModel, selectedOperation)}
                value={getAspectRatiosForModel(selectedModel, selectedOperation).includes(aspectRatio) ? aspectRatio : selectedModel.defaultAspectRatio}
                onChange={setAspectRatio}
              />
            </div>

            {/* Duration */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <label className="text-sm font-medium text-text-secondary">{t('generator.duration')}</label>
                <InfoTooltip text={t(SETTING_TOOLTIP_KEYS.duration)} />
              </div>
              <PillSelector
                options={selectedOperation === 'multiple_images' ? ['8s'] : selectedModel.durations}
                value={selectedOperation === 'multiple_images' ? '8s' : (selectedModel.durations.includes(duration) ? duration : selectedModel.defaultDuration)}
                onChange={setDuration}
              />
            </div>

            {/* Quality / Resolution */}
            {selectedModel.resolutions.length > 1 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <label className="text-sm font-medium text-text-secondary">{t('generator.quality')}</label>
                  <InfoTooltip text={t(SETTING_TOOLTIP_KEYS.quality)} />
                </div>
                <PillSelector
                  options={selectedModel.resolutions}
                  value={selectedModel.resolutions.includes(settings.resolution) ? settings.resolution : selectedModel.defaultResolution}
                  onChange={(v) => updateSetting('resolution', v)}
                />
              </div>
            )}

          </div>

          {/* Advanced Settings (collapsible) */}
          {(selectedModel.supportsAudio || selectedModel.supportsAutoFix || selectedModel.supportsNegativePrompt || selectedModel.supportsSeed) && (
            <div className="bg-surface-light rounded-2xl border border-glass-border overflow-hidden">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between px-5 py-4"
              >
                <span className="text-sm font-semibold text-text-primary">{t('generator.advancedSettings')}</span>
                <ChevronDown className={cn('w-4 h-4 text-text-secondary transition-transform', showAdvanced && 'rotate-180')} />
              </button>

              {showAdvanced && (
                <div className="px-5 pb-5 space-y-5">
                  {/* Audio toggle */}
                  {selectedModel.supportsAudio && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-text-secondary">{t('generator.generateAudio')}</label>
                        <InfoTooltip text={t(SETTING_TOOLTIP_KEYS.audio)} />
                      </div>
                      <Switch checked={generateAudio} onChange={setGenerateAudio} />
                    </div>
                  )}

                  {/* Auto-fix toggle */}
                  {selectedModel.supportsAutoFix && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-text-secondary">{t('generator.autoFix')}</label>
                        <InfoTooltip text={t(SETTING_TOOLTIP_KEYS.autoFix)} />
                      </div>
                      <Switch checked={autoFix} onChange={setAutoFix} />
                    </div>
                  )}

                  {/* Negative Prompt */}
                  {selectedModel.supportsNegativePrompt && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm font-medium text-text-secondary">{t('generator.negativePrompt')}</label>
                        <InfoTooltip text={t(SETTING_TOOLTIP_KEYS.negativePrompt)} />
                      </div>
                      <textarea
                        value={negativePrompt}
                        onChange={(e) => setNegativePrompt(e.target.value)}
                        placeholder={t('generator.negativePromptHint')}
                        rows={2}
                        className="w-full bg-surface border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary resize-none"
                      />
                    </div>
                  )}

                  {/* Seed */}
                  {selectedModel.supportsSeed && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm font-medium text-text-secondary">{t('generator.seed')}</label>
                        <InfoTooltip text={t(SETTING_TOOLTIP_KEYS.seed)} />
                      </div>
                      <input
                        type="number"
                        value={seed}
                        onChange={(e) => setSeed(e.target.value)}
                        placeholder={t('generator.randomSeed')}
                        className="w-full bg-surface border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Generate Button with credits */}
          <div className="sticky bottom-6 mb-8 md:mb-0">
            <Button
              size="lg"
              className="w-full relative"
              onClick={handleGenerate}
              disabled={!isValid || isGenerating || isUploading}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t('common.generating')}
                </>
              ) : isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t('status.uploadingFile')}
                </>
              ) : (
                t('home.createVideo')
              )}
              <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-bold bg-white/10 rounded">
                {currentCreditCost} {t('account.credits').toLowerCase()}
              </span>
            </Button>
            {/* Star emoji for mobile */}
            <div className="md:hidden text-center mt-4 text-2xl"> </div>
          </div>
        </div>
      )}

      {/* Preview Loading Screen */}
      <PreviewLoadingScreen
        isOpen={showPreviewLoading}
        previewImageUrl={previewImageUrl}
        onComplete={() => {
          setShowPreviewLoading(false);
          setPreviewImageUrl(undefined);
          navigate('/files?highlight=true');
        }}
      />

      {/* v2 Plan Modal — only ever opens when uiVersion === 'v2'. */}
      <PlanModal
        isOpen={planModal.open}
        onClose={() => setPlanModal({ open: false })}
        onApproved={handlePlanApproved}
        seed={planModal.seed}
        resumeOrderId={planModal.resumeOrderId}
      />

      {/* Premium Model Modal */}
      <Modal isOpen={showPremiumModelModal} onClose={() => setShowPremiumModelModal(false)} title={t('subscription.premium')} size="sm">
        <div className="flex flex-col items-center text-center gap-4 py-4">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Crown className="w-7 h-7 text-amber-500" />
          </div>
          <div className="space-y-2">
            <p className="text-base font-semibold text-text-primary">
              {t('models.premiumModelTitle')}
            </p>
            <p className="text-sm text-text-secondary">
              {t('models.premiumModelDesc')}
            </p>
          </div>
          <Button
            onClick={() => {
              setShowPremiumModelModal(false);
              trackPaywallCtaClicked('premium_model');
              navigate('/subscription?context=premium_model');
            }}
            className="w-full"
          >
            <Crown className="w-4 h-4 mr-2" />
            {t('home.upgradeToPro')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
